import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { psychologistSelectorById } from '../../redux/specialists/selector';
import { selectConsultationsBySpecialistAndClient } from '../../redux/consultations/selector';
import { useRemoteConfig } from '../../hooks/useRemoteConfig/useRemoteConfig';

type UseIsClientCanBookSessionOptions = {
  specialistId: string;
  clientId: string;
};

/**
 * Hook to determine if the client can book a session with the specialist
 *
 * @param {UseIsClientCanBookSessionOptions} options - options for the hook
 * @returns {{
 *   isShowFullBookingControls: boolean;
 *   isShowChat: boolean;
 *   isSpecialistUnAvailable: boolean;
 * }} - object with properties indicating if the full booking controls, chat, and specialist is unavailable
 */
export function useIsClientCanBookSession({ specialistId, clientId }: UseIsClientCanBookSessionOptions): {
  isShowFullBookingControls: boolean;
  isShowChat: boolean;
  isSpecialistUnAvailable: boolean;
  isDisabledBooking: boolean;
} {
  const { isActiveFeature } = useRemoteConfig();
  const [isEnabled, setIsEnabled] = React.useState(false);
  const specialist = useSelector(psychologistSelectorById(specialistId));
  const consultations = useSelector(selectConsultationsBySpecialistAndClient(specialistId, clientId));

  useEffect(() => {
    isActiveFeature('inactive_specialist_view_enabled').then((value) => {
      setIsEnabled(value);
    });
  }, []);

  return useMemo(() => {
    if (!isEnabled) {
      return {
        isShowFullBookingControls: true,
        isShowChat: false,
        isSpecialistUnAvailable: false,
        isDisabledBooking: false,
      };
    }

    let isShowFullBookingControls = false;
    let isShowChat = false;
    let isSpecialistUnAvailable = false;

    const hasConsultations = (consultations?.length ?? 0) > 0;

    if (hasConsultations || !!specialist?.is_public) {
      isShowFullBookingControls = true;
    } else if (!!specialist?.is_active && hasConsultations) {
      isShowFullBookingControls = true;
    }

    if (!specialist?.is_public && !hasConsultations) {
      isShowChat = true;
      isShowFullBookingControls = false;
    }
    if (!specialist?.is_active && !hasConsultations) {
      isShowChat = false;
      isShowFullBookingControls = false;
      isSpecialistUnAvailable = true;
    }

    return {
      isShowFullBookingControls,
      isShowChat,
      isSpecialistUnAvailable,
      isDisabledBooking: !specialist?.is_active || (typeof specialist.is_public === 'boolean' && !specialist.is_public),
    };
  }, [isEnabled, consultations?.length, specialist?.is_active, specialist?.is_public]);
}
